import { resolveRecordPath } from './record-list.utils';
import { RecordListColumn, RecordListState, RecordListTree, RecordListTreeRow } from './record-list.types';

type Row = Record<string, unknown>;

/** Pure projection: never fetch inaccessible ancestors or synthesize missing identities. */
export function projectRecordTree(records: Row[], tree: RecordListTree, columns: RecordListColumn[], state: RecordListState, collapsed: readonly string[]) {
  const value = (row: Row, key: string) => resolveRecordPath(row, key).value;
  const id = (row: Row) => String(value(row, tree.idKey) ?? '');
  const parent = (row: Row) => String(value(row, tree.parentKey) ?? '');
  const byId = new Map<string, Row>();
  for (const row of records) {
    if (!id(row) || byId.has(id(row))) throw new Error('Tree records require unique non-empty IDs.');
    if (!resolveRecordPath(row, tree.parentKey).found) throw new Error('Tree records are missing their parent field.');
    byId.set(id(row), row);
  }
  // Validate every chain, including disconnected cycles, with linear-time marking.
  const done = new Set<string>();
  for (const key of byId.keys()) {
    const path = new Set<string>();
    let next = key;
    while (byId.has(next) && !done.has(next)) {
      if (path.has(next)) throw new Error('The record hierarchy contains a cycle. Correct its parent relationships and retry.');
      path.add(next);
      next = parent(byId.get(next)!);
    }
    path.forEach(entry => done.add(entry));
  }
  const children = new Map<string, Row[]>();
  for (const row of records) {
    const key = parent(row);
    children.set(key, [...(children.get(key) ?? []), row]);
  }
  const sort = state.sort;
  const compare = (left: Row, right: Row) => {
    const a = sort ? value(left, sort.key) : id(left);
    const b = sort ? value(right, sort.key) : id(right);
    const result = typeof a === 'number' && typeof b === 'number' ? a - b
      : String(a ?? '').localeCompare(String(b ?? ''), undefined, {numeric:true});
    return (sort?.direction === 'desc' ? -result : result) || id(left).localeCompare(id(right));
  };
  children.forEach(siblings => siblings.sort(compare));
  const roots = tree.rootParentId !== undefined ? children.get(tree.rootParentId) ?? []
    : records.filter(row => !byId.has(parent(row))).sort(compare);
  const scoped: Row[] = [];
  const pending = [...roots];
  while (pending.length) {
    const row = pending.pop()!;
    scoped.push(row);
    pending.push(...(children.get(id(row)) ?? []));
  }
  const scopedIds = new Set(scoped.map(id));
  const matched = new Set(scoped.filter(row => recordMatchesState(row, columns, state)).map(id));
  const included = new Set(matched);
  for (const key of matched) {
    let next = parent(byId.get(key)!);
    while (scopedIds.has(next) && !included.has(next)) {
      included.add(next);
      next = parent(byId.get(next)!);
    }
  }
  const filteredRoots = roots.filter(row => included.has(id(row)));
  // Page whole root subtrees so a child is never stranded on another page.
  const total = filteredRoots.length;
  const start = (state.page - 1) * state.perPage;
  const rows: RecordListTreeRow[] = [];
  const searching = Boolean(state.search || Object.keys(state.filters).length);
  const stack = filteredRoots.slice(start, start + state.perPage).reverse().map(row => ({row, depth:0}));
  const collapsedSet = new Set(collapsed);
  while (stack.length) {
    const {row, depth} = stack.pop()!;
    const descendants = (children.get(id(row)) ?? []).filter(child => included.has(id(child)));
    const expanded = searching || !collapsedSet.has(id(row));
    rows.push({row, id:id(row), depth, hasChildren:descendants.length > 0, expanded, contextOnly:!matched.has(id(row))});
    if (expanded) stack.push(...descendants.slice().reverse().map(child => ({row:child, depth:depth + 1})));
  }
  return {rows, total, matchedCount:matched.size, scoped};
}

export function recordMatchesState(row: Row, columns: RecordListColumn[], state: RecordListState): boolean {
  const actual = (column: RecordListColumn) => resolveRecordPath(row, column.key).value;
  if (state.search && !columns.some(column => column.filter?.searchable && String(actual(column) ?? '').toLocaleLowerCase().includes(state.search.toLocaleLowerCase()))) return false;
  return Object.entries(state.filters).every(([key, filter]) => {
    const column = columns.find(candidate => candidate.key === key && candidate.filter?.filterable);
    if (!column) return true;
    const values = Array.isArray(filter.value) ? filter.value : [filter.value];
    if (filter.operator === 'eq') return values.some(expected => String(expected) === String(actual(column)));
    const numeric = ['number','integer','currency','percentage'].includes(column.filter?.valueType ?? '') || typeof actual(column) === 'number';
    const convert = (v: unknown) => numeric ? Number(v) : Date.parse(String(v));
    const left = convert(actual(column));
    const right = values.map(convert);
    if (!Number.isFinite(left) || right.some(v => !Number.isFinite(v))) return false;
    switch (filter.operator) {
      case 'between': return left >= right[0] && left <= right[1];
      case 'gt': return left > right[0];
      case 'gte': return left >= right[0];
      case 'lt': return left < right[0];
      case 'lte': return left <= right[0];
    }
  });
}
